import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import {
  DvDropdownClientSideElementParams,
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvDynamicFormUtils,
  DvFormUtils,
  FormOf,
} from '@devinco/ui/legacy';
import { TripExpenseDto } from '@pages/trips-page/components/trip-expenses-tab/interfaces';
import { TripType } from '@pages/trips-page/constants';
import { combineLatest, take } from 'rxjs';
import { ExpenseEditDialogFormService } from './expense-edit-dialog-form.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Entity } from '@shared/constants/entity';
import { PipesModule } from '@shared/pipes/pipes.module';
import {
  DvButtonComponent,
  DvDialogContent,
  DvDialogContentComponent,
} from '@devinco/ui/dds';

@Component({
  imports: [
    CommonModule,
    DvDynamicFormModule,
    PipesModule,
    DvButtonComponent,
    TranslateModule,
    DvDialogContentComponent,
  ],
  selector: 'app-expense-edit-dialog',
  templateUrl: './expense-edit-dialog.component.html',
  styleUrls: ['./expense-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ExpenseEditDialogFormService],
})
export class ExpenseEditDialogComponent
  extends DvDialogContent<Partial<TripExpenseDto>, ExpenseEditDialogComponent>
  implements OnInit
{
  private expenseModalService = inject(ExpenseEditDialogFormService);
  private api = inject(RestApiService);

  formGroup: FormGroup<FormOf<TripExpenseDto>>;
  upperFormElements: DvDynamicFormElement[];
  lowerFormElements: DvDynamicFormElement[];
  showResetQuantityBtn: boolean;

  /** initially (if edit dialog opened) value is take from dialog data object
   * If user makes changes, value is taken from ExpenseSetting entity (dropdown)
   */
  private rateEnabled: boolean;
  /** initially (if edit dialog opened) value is take from dialog data object
   * If user makes changes, value is taken from ExpenseSetting entity (dropdown)
   */
  private tripDistanceConnection: boolean;
  private destroyRef = inject(DestroyRef);
  ngOnInit(): void {
    this.setupForm();
  }

  onResetQuantityClicked(): void {
    const { Quantity } = this.formGroup.controls;
    Quantity?.setValue(this.data.Distance);
    Quantity?.markAsDirty();
    this.showResetQuantityBtn = false;
  }

  performSave(): void {
    this.api
      .upsert(Entity.TripExpense, this.data.TripExpenseID!, {
        ...this.formGroup.getRawValue(),
        TripID: Number(this.data.TripID),
      })
      .pipe(take(1))
      .subscribe(tripExpenseId => {
        this.dialogRef.onConfirm({
          ...this.formGroup.getRawValue(),
          TripExpenseID: tripExpenseId ?? this.data.TripExpenseID,
          RateEnabled: this.rateEnabled,
          TripDistanceConnection: this.tripDistanceConnection,
          Description: this.data.TripExpenseID
            ? this.data.Description
            : this.getSelectedDropdownItemData(
                this.formGroup.value.ExpenseSettingID!
              )?.ExpenseType,
        });
      });
  }

  cancel(): void {
    this.dialogRef.onCancel();
  }

  private setupForm(): void {
    this.upperFormElements = this.expenseModalService.upperFormElements(
      this.data.TripID!
    );
    this.lowerFormElements = this.expenseModalService.lowerFormElements;
    this.formGroup = DvFormUtils.createFormGroup(
      [...this.upperFormElements, ...this.lowerFormElements],
      true
    );
    if (this.data.TripType === TripType.Corporate) {
      this.formGroup.controls.IsPrivateTaxable.disable();
    }

    this.listenToRateAndQuantityChanges();
    DvDynamicFormUtils.setFirstData(
      [...this.upperFormElements, ...this.lowerFormElements],
      this.formGroup,
      this.data
    );
    this.rateEnabled = !!this.data.RateEnabled;
    this.tripDistanceConnection = !!this.data.TripDistanceConnection;
    this.toggleFieldsForRateIsLocked(
      false,
      this.rateEnabled,
      this.tripDistanceConnection
    );
    this.listenToExpenseTypeChanges();
    this.toggleExpenseSettingDropdown(this.data.TripExpenseID!);
  }

  private listenToRateAndQuantityChanges(): void {
    const rateValueChanges = this.formGroup.controls.Rate?.valueChanges;
    const quantityValueChanges = this.formGroup.controls.Quantity?.valueChanges;

    if (!rateValueChanges || !quantityValueChanges) return;

    combineLatest([rateValueChanges, quantityValueChanges])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([rate, quantity]) => {
        this.showResetQuantityBtn =
          this.formGroup.controls.Quantity?.value !== this.data.Distance &&
          !this.rateEnabled &&
          this.tripDistanceConnection;
        const sum = rate && quantity ? Number((rate * quantity).toFixed(2)) : 0;
        this.formGroup.controls.Amount?.setValue(sum, { emitEvent: false });
      });
  }

  private listenToExpenseTypeChanges(): void {
    this.formGroup.controls.ExpenseSettingID.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const selectedDropdownItemData =
          this.getSelectedDropdownItemData(value);
        this.rateEnabled = !!selectedDropdownItemData?.RateEnabled;
        this.tripDistanceConnection =
          !!selectedDropdownItemData?.TripDistanceConnection;

        this.toggleFieldsForRateIsLocked(
          true,
          this.rateEnabled,
          this.tripDistanceConnection
        );

        if (
          this.isDistanceBasedExpenseType(
            selectedDropdownItemData?.TranslationKey
          )
        ) {
          this.formGroup.controls.Quantity?.setValue(this.data.Distance);
        }
      });
  }

  private isDistanceBasedExpenseType(translationKey?: string): boolean {
    return (
      translationKey === 'ExpenseType.Passenger' ||
      translationKey === 'ExpenseType.Trailer'
    );
  }

  /** if expense type's rateEnabled === false, then:
   * 1) price is disabled
   *
   * if expense type's rateEnabled === true, then:
   * 1) rate is enabled
   *
   * When TripDistanceConnection === false, then:
   * 1) expense is not connected to the distance of the trip
   * 2) "reset quantity" button hidden
   *
   * When TripDistanceConnection === true, then:
   * 1) expense is connected to the distance of the trip
   * 2) quantity's ranges are from 0 to trip's distance
   */
  private toggleFieldsForRateIsLocked(
    isEditing = false,
    rateEnabled: boolean,
    isExpenceConnectedToDistance: boolean
  ): void {
    const { controls } = this.formGroup;
    this.toggleRate(rateEnabled);

    if (isEditing) {
      controls.Rate?.setValue(
        this.getSelectedDropdownItemData(controls.ExpenseSettingID.value)
          ?.Rate || 0
      );
    }

    if (isExpenceConnectedToDistance) {
      if (isEditing && this.data.Distance) {
        controls.Quantity?.setValidators([
          Validators.min(0),
          Validators.max(this.data.Distance),
        ]);
        controls.Quantity?.setValue(this.data.Distance);
      }
      if (controls.Quantity?.value !== this.data.Distance) {
        this.showResetQuantityBtn = true;
      }
    } else if (isEditing) {
      this.showResetQuantityBtn = false;
      controls.Quantity?.setValue(1);
    }
  }

  private toggleRate(rateEnabled: boolean): void {
    const { Rate } = this.formGroup.controls;
    if (!rateEnabled) {
      Rate?.disable({ emitEvent: false });
    } else {
      Rate?.enable({ emitEvent: false });
    }
  }

  private getSelectedDropdownItemData(
    itemId: number
  ): Partial<TripExpenseDto> | undefined {
    return (
      this.upperFormElements.find(el => el.options.key === 'ExpenseSettingID')
        ?.options as DvDropdownClientSideElementParams
    ).api
      ?.getItems()
      .find(item => item.ExpenseSettingID === itemId);
  }

  private toggleExpenseSettingDropdown(tripExpenseId: number): void {
    if (tripExpenseId) {
      this.formGroup.controls.ExpenseSettingID.disable({ emitEvent: false });
    }
  }
}
